import { useRef } from "react";
import { CATEGORIES } from "@oshimap/config";
import type { MarkerViewModel } from "@oshimap/map-core";
import { CategoryChip, VerificationBadge, CategoryIcon } from "../../components/ui";
import { play, haptic, sparkle } from "../../lib/motion";
import { LiveGlance } from "../live/LiveGlance";

interface Props {
  m: MarkerViewModel;
  selected?: boolean;
  onOpen: (m: MarkerViewModel) => void;
  /** 토글 후의 즐겨찾기 상태. true 면 방금 저장된 것이므로 SPARKLE 을 띄운다. */
  onToggleFavorite: (m: MarkerViewModel) => Promise<boolean>;
}

export function PlaceCard({ m, selected, onOpen, onToggleFavorite }: Props) {
  const favRef = useRef<HTMLButtonElement>(null);
  const def = CATEGORIES[m.category];
  return (
    <div className="om-place" aria-current={selected ? "true" : undefined}>
      <button className="om-place__thumb" onClick={() => onOpen(m)} aria-hidden="true" tabIndex={-1}>
        <CategoryIcon code={m.category} size={22} />
      </button>
      <button className="om-place__body" onClick={() => onOpen(m)} style={{ textAlign: "left" }}>
        <span className="om-place__name">{m.name}</span>
        <span className="om-place__row">
          <CategoryChip code={m.category} />
          {m.distanceLabel ? <span className="om-place__dist num">{m.distanceLabel}</span> : null}
        </span>
        <span className="om-place__row">
          <VerificationBadge status={m.verification} />
          {/*
            * "어디로 갈까" 를 정하는 곳은 상세가 아니라 이 카드다.
            * 선택된 카드에만 붙인다 — 목록 전체에 붙이면 장소마다 요청이 나간다.
            */}
          {selected && !m.isEvent ? <LiveGlance placeId={m.id} /> : null}
          {m.activeEventCount > 0 ? (
            <span className="om-place__dist">진행 중 <b className="num">{m.activeEventCount}</b></span>
          ) : null}
          {m.hasVisited ? <span className="om-place__dist">방문함</span> : null}
        </span>
      </button>
      <button
        ref={favRef}
        className="om-fav"
        aria-pressed={m.isFavorited}
        aria-label={m.isFavorited ? `${m.name} 저장 해제` : `${m.name} 저장`}
        onClick={async () => {
          play(favRef.current, "mo-pop");
          haptic("POP");
          const nowFavorited = await onToggleFavorite(m);
          if (nowFavorited) sparkle(favRef.current, 5);   // 추가는 SPARKLE, 해제는 POP 만 (15 §4)
        }}
      >
        {m.isFavorited ? "♥" : "♡"}
      </button>
    </div>
  );
}
